/**
 * Every derived value in the game. Pure functions only: no state mutation,
 * no side effects, no reads of anything but the arguments.
 *
 * The UI displays numbers from here and the simulation consumes numbers from
 * here, so what the player sees always matches what is actually running.
 */

import Decimal from 'break_infinity.js';
import { GENERATORS, type GeneratorDef } from '../data/generators.ts';
import { SKILLS_BY_ID, type Modifier } from '../data/skills.ts';
import {
  BASE_OUTPUT,
  CELL_HEAT,
  FREE_UNFED,
  UNLOCK_PREVIOUS_COUNT,
  CLOCK_CAP_BASE,
  CLOCK_EXP,
  CLOCK_MIN,
  COOLING_BASE,
  COST_GROWTH,
  GM_DIV,
  GM_EXP,
  GM_MIN_THOUGHTS,
  GM_SCALE,
  HEAT_BASE,
  HEAT_EXP,
  HEAT_MAX,
  MILESTONE_EVERY,
  MILESTONE_MULT,
  OFFLINE_CAP_SECONDS,
  OFFLINE_RATE,
  REVEAL_FRACTION,
  THERMAL_MAX,
  UNDERFED_RATE,
  VOID_AFTER_MELTDOWNS,
  VOID_HEAT_MAX,
  VOID_HOLD_SECONDS,
  VOID_ONSET_SECONDS,
  VOID_PRESSURE_THRESHOLD,
  VENT_AMOUNT,
  VENT_COOLDOWN,
} from '../data/balance.ts';
import type { GameState } from './state.ts';

const ONE = new Decimal(1);
const ZERO = new Decimal(0);

// ---------------------------------------------------------------------------
// Skill modifiers
// ---------------------------------------------------------------------------

/** Every tunable the skill tree can move, already resolved for this state. */
export interface Modifiers {
  tpsMult: number;
  cooling: number;
  ventAmount: number;
  ventCooldown: number;
  heatExp: number;
  clockCap: number;
  clockExp: number;
  idleOutputMult: number;
  retainFraction: number;
  gmMult: number;
  /** Multiplies how long a run holds the void off. */
  voidResist: number;
  offlineCapSeconds: number;
  offlineRate: number;
  flags: Record<string, boolean>;
}

function baseModifiers(): Modifiers {
  return {
    tpsMult: 1,
    cooling: COOLING_BASE,
    ventAmount: VENT_AMOUNT,
    ventCooldown: VENT_COOLDOWN,
    heatExp: HEAT_EXP,
    clockCap: CLOCK_CAP_BASE,
    clockExp: CLOCK_EXP,
    idleOutputMult: 1,
    retainFraction: 0,
    gmMult: 1,
    voidResist: 1,
    offlineCapSeconds: OFFLINE_CAP_SECONDS,
    offlineRate: OFFLINE_RATE,
    flags: {},
  };
}

/**
 * Folds one declared modifier into the accumulator.
 *
 * `set` modifiers resolve to whichever value is strictly better for the
 * player, so buying skills out of order can never make the rig worse.
 */
function applyModifier(m: Modifiers, mod: Modifier, rank: number): void {
  switch (mod.kind) {
    case 'tps':
      m.tpsMult *= Math.pow(mod.mult, rank);
      break;
    case 'cooling':
      m.cooling += mod.add * rank;
      break;
    case 'ventAmount':
      m.ventAmount = Math.max(m.ventAmount, mod.set);
      break;
    case 'ventCooldown':
      m.ventCooldown = Math.min(m.ventCooldown, mod.set);
      break;
    case 'heatExp':
      m.heatExp = Math.min(m.heatExp, mod.set);
      break;
    case 'clockCap':
      m.clockCap = Math.max(m.clockCap, mod.set);
      break;
    case 'clockExp':
      m.clockExp = Math.max(m.clockExp, mod.set);
      break;
    case 'idleOutput':
      m.idleOutputMult *= Math.pow(mod.mult, rank);
      break;
    case 'retain':
      m.retainFraction += mod.add * rank;
      break;
    case 'gmGain':
      m.gmMult += mod.add * rank;
      break;
    case 'voidResist':
      m.voidResist *= Math.pow(mod.mult, rank);
      break;
    case 'offline':
      m.offlineCapSeconds = Math.max(m.offlineCapSeconds, mod.capSeconds);
      m.offlineRate = Math.max(m.offlineRate, mod.rate);
      break;
    case 'unlock':
      m.flags[mod.flag] = true;
      break;
  }
}

/** Resolves every owned skill into a single set of tunables. */
export function getModifiers(state: GameState): Modifiers {
  const m = baseModifiers();
  for (const [id, rank] of Object.entries(state.skills)) {
    if (rank <= 0) continue;
    const def = SKILLS_BY_ID[id];
    if (def === undefined) continue;
    const effects = Array.isArray(def.modifier) ? def.modifier : [def.modifier];
    for (const effect of effects) applyModifier(m, effect, rank);
  }
  return m;
}

/** Whether the prerequisite chain for a skill is satisfied. */
export function skillAvailable(state: GameState, id: string): boolean {
  const def = SKILLS_BY_ID[id];
  if (def === undefined) return false;
  if (def.req === undefined) return true;
  return (state.skills[def.req] ?? 0) > 0;
}

// ---------------------------------------------------------------------------
// Generator cost
// ---------------------------------------------------------------------------

/**
 * Cost to buy `count` more units of a generator that already has `owned`.
 *
 * Closed-form geometric sum:
 *   baseCost * growth^owned * (growth^count - 1) / (growth - 1)
 *
 * Never loop over units here. At high counts a loop hitches the frame, and
 * Max-buy has to be a single computation.
 */
export function costOf(def: GeneratorDef, owned: number, count: number): Decimal {
  if (count <= 0) return ZERO;
  const growth = new Decimal(COST_GROWTH);
  const first = new Decimal(def.baseCost).mul(growth.pow(owned));
  if (count === 1) return first;
  return first.mul(growth.pow(count).sub(ONE)).div(growth.sub(ONE));
}

/** Cost of the single next unit of generator `index`. */
export function costOfNext(state: GameState, index: number): Decimal {
  const def = GENERATORS[index];
  if (def === undefined) return ZERO;
  return costOf(def, state.generators[index] ?? 0, 1);
}

/**
 * How many units of a generator `money` can buy, in one computation.
 *
 * Inverts the geometric sum:
 *   k = floor( log_g( 1 + money * (g - 1) / (baseCost * g^owned) ) )
 */
export function maxAffordable(def: GeneratorDef, owned: number, money: Decimal): number {
  const growth = new Decimal(COST_GROWTH);
  const first = new Decimal(def.baseCost).mul(growth.pow(owned));
  if (money.lt(first)) return 0;

  const ratio = money.mul(growth.sub(ONE)).div(first).add(ONE);
  const k = Math.floor(ratio.log10() / Math.log10(COST_GROWTH));
  return Number.isFinite(k) && k > 0 ? k : 0;
}

/**
 * Resolves a bulk-buy selection into a unit count.
 * `mode` is 1, 10, 100, or 'max'.
 */
export function bulkCount(
  state: GameState,
  index: number,
  mode: number | 'max',
): number {
  const def = GENERATORS[index];
  if (def === undefined) return 0;
  const owned = state.generators[index] ?? 0;
  const room = roomToBuy(state, index);
  if (room <= 0) return 0;
  const wanted = mode === 'max' ? maxAffordable(def, owned, state.thoughts) : mode;
  // Clamped rather than refused: buying three when ten were asked for is
  // friendlier than a dead button, and the row quotes the real figure.
  return Math.min(wanted, room);
}

/**
 * A tier stays hidden until the player has held a fraction of its base cost.
 * Latched through `unlocks`, so a tier never disappears again once shown.
 */
export function revealThreshold(def: GeneratorDef): Decimal {
  return new Decimal(def.baseCost).mul(REVEAL_FRACTION);
}

export function generatorUnlockKey(def: GeneratorDef): string {
  return `gen:${def.id}`;
}

/**
 * Whether a tier can be bought at all yet.
 *
 * A hard prerequisite rather than a soft penalty, and the one place a hard gate
 * belongs: it refuses the purchase instead of taking the money for something
 * that does nothing. The ladder has to be climbed a rung at a time.
 */
export function tierUnlocked(state: GameState, index: number): boolean {
  if (index <= 0) return true;
  return (state.generators[index - 1] ?? 0) >= UNLOCK_PREVIOUS_COUNT;
}

/** Units of the tier below still needed before this one opens. */
export function unitsToUnlock(state: GameState, index: number): number {
  if (index <= 0) return 0;
  return Math.max(0, UNLOCK_PREVIOUS_COUNT - (state.generators[index - 1] ?? 0));
}

/**
 * How many more units of a tier the rung below can currently carry.
 *
 * The unlock check only fires once, so on its own it let a player open a tier
 * with ten Neurons and then buy it without limit - 31 Synapses resting on 26
 * Axons. Capacity has to be enforced on every purchase, not just the first.
 */
export function roomToBuy(state: GameState, index: number): number {
  if (!tierUnlocked(state, index)) return 0;
  const capacity = supportCapacity(state, index);
  if (!Number.isFinite(capacity)) return Number.POSITIVE_INFINITY;
  return Math.max(0, capacity - (state.generators[index] ?? 0));
}

/** Units of the tier below needed before one more of this tier will fit. */
export function feedShortfall(state: GameState, index: number): number {
  const def = GENERATORS[index];
  const needs = def?.needs ?? 0;
  if (needs <= 0) return 0;
  const have = state.generators[index - 1] ?? 0;
  const wanted = ((state.generators[index] ?? 0) + 1 - FREE_UNFED) * needs;
  return Math.max(0, wanted - have);
}

// ---------------------------------------------------------------------------
// Milestones
// ---------------------------------------------------------------------------

/**
 * A tier doubles its own output every MILESTONE_EVERY units owned.
 *
 * Without this every row is the same shrug: a bigger number for a bigger
 * price. With it each row carries a visible next goal, and going deeper on a
 * tier you already own becomes a real alternative to opening the next one.
 */
export function milestoneMult(count: number): Decimal {
  if (count < MILESTONE_EVERY) return ONE;
  // Decimal, not Math.pow: the exponent is count/25, which overflows a double
  // to Infinity long before Decimal runs out of room, and one Infinity poisons
  // the whole production sum.
  return new Decimal(MILESTONE_MULT).pow(Math.floor(count / MILESTONE_EVERY));
}

/** Units still needed for this tier's next doubling. */
export function unitsToMilestone(count: number): number {
  return MILESTONE_EVERY - (count % MILESTONE_EVERY);
}

/** The multiplier this tier will sit at once the next milestone lands. */
export function nextMilestoneMult(count: number): Decimal {
  return milestoneMult(count).mul(MILESTONE_MULT);
}

/**
 * Raw output that buying `units` more of a tier would add, in base units/s.
 *
 * Two contributions, and the second is the whole reason the support chain works
 * as a mechanic: a Dendrite produces on its own, and it also lifts ten stranded
 * Neurons up to full rate. Counting only the first would make every support
 * tier look like a bad buy and quietly teach the player to ignore it.
 */
export function marginalOutput(state: GameState, index: number, units = 1): Decimal {
  const def = GENERATORS[index];
  if (def === undefined || units <= 0) return ZERO;

  const count = state.generators[index] ?? 0;
  const capacity = supportCapacity(state, index);

  const before = new Decimal(def.baseOutput)
    .mul(effectiveUnits(count, capacity))
    .mul(milestoneMult(count));
  const after = new Decimal(def.baseOutput)
    .mul(effectiveUnits(count + units, capacity))
    .mul(milestoneMult(count + units));
  let gain = after.sub(before);

  // What these units feed in the tier above them. Without this term a Neuron
  // looks worthless next to the row it exists to keep running, and the panel
  // would teach the player to skip the base entirely.
  const above = GENERATORS[index + 1];
  const aboveNeeds = above?.needs ?? 0;
  if (above !== undefined && aboveNeeds > 0) {
    const aboveCount = state.generators[index + 1] ?? 0;
    const nowFeeds = supportCapacity(state, index + 1);
    const thenFeeds = FREE_UNFED + Math.floor((count + units) / aboveNeeds);
    const freed = new Decimal(above.baseOutput)
      .mul(effectiveUnits(aboveCount, thenFeeds) - effectiveUnits(aboveCount, nowFeeds))
      .mul(milestoneMult(aboveCount));
    gain = gain.add(freed);
  }

  return gain;
}

/**
 * How much the average heat factor would improve by buying into a tier.
 *
 * Above 1 means the purchase makes the rig run cooler per spark. Without this
 * term the value metric cannot see the one reason to climb - it only ever
 * compares raw output for money, and the base tier wins that forever.
 */
function heatDiscount(state: GameState, index: number, units: number): number {
  const before = cellHeat(state);
  if (before <= 0) return 1;
  const generators = [...state.generators];
  generators[index] = (generators[index] ?? 0) + units;
  const after = cellHeat({ ...state, generators });
  if (after <= 0) return 1;
  return before / after;
}

/**
 * Output per thought spent on a tier, at the best purchase size available.
 *
 * Judging a tier only by its very next unit is myopic: the unit before a
 * milestone looks worthless even though it is one step from multiplying the
 * whole row. So each tier is also priced as a walk to its next milestone, and
 * scored on whichever of the two is the better deal. Without this the panel
 * recommends buys that measurably lose to a naive strategy.
 */
export function efficiency(state: GameState, index: number): number {
  const def = GENERATORS[index];
  if (def === undefined) return 0;

  const count = state.generators[index] ?? 0;
  const sizes = [1];
  const toMilestone = unitsToMilestone(count);
  if (toMilestone > 1) sizes.push(toMilestone);

  let best = 0;
  for (const units of sizes) {
    const price = costOf(def, count, units);
    if (price.lte(0)) continue;
    const raw = marginalOutput(state, index, units).div(price).toNumber();
    const value = raw * heatDiscount(state, index, units);
    if (Number.isFinite(value) && value > best) best = value;
  }
  return best;
}

/**
 * The revealed, affordable tier giving the most output per thought.
 *
 * Affordability is part of the definition on purpose: this drives a marker on a
 * buy button, so recommending something the player cannot click is worse than
 * useless. Returns -1 when nothing is affordable.
 */
export function bestBuy(state: GameState): number {
  let best = -1;
  let bestValue = 0;
  for (let i = 0; i < GENERATORS.length; i++) {
    const def = GENERATORS[i];
    if (def === undefined) continue;
    if (state.unlocks[generatorUnlockKey(def)] !== true) continue;
    if (roomToBuy(state, i) <= 0) continue;
    if (state.thoughts.lt(costOf(def, state.generators[i] ?? 0, 1))) continue;

    const value = efficiency(state, i);
    if (value > bestValue) {
      bestValue = value;
      best = i;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Support chain
// ---------------------------------------------------------------------------

/**
 * How many units of tier `index` the tier below it can currently feed.
 *
 * Neurons are the base: nothing feeds them and they need nothing, so they
 * return Infinity.
 */
export function supportCapacity(state: GameState, index: number): number {
  const def = GENERATORS[index];
  if (def === undefined) return 0;
  const needs = def.needs ?? 0;
  if (needs <= 0) return Number.POSITIVE_INFINITY;
  return FREE_UNFED + Math.floor((state.generators[index - 1] ?? 0) / needs);
}

/**
 * Units of a tier weighted for feeding: fed ones count fully, the rest at
 * UNDERFED_RATE. This is what production is actually computed from.
 */
export function effectiveUnits(count: number, capacity: number): number {
  if (count <= 0) return 0;
  const fed = Math.min(count, capacity);
  const starved = count - fed;
  return fed + starved * UNDERFED_RATE;
}

export interface SupportState {
  count: number;
  capacity: number;
  /** Units firing below full rate for want of the tier beneath them. */
  stranded: number;
  /** 0..1, how much of this tier is fed. */
  fraction: number;
}

export function supportState(state: GameState, index: number): SupportState {
  const count = state.generators[index] ?? 0;
  const capacity = supportCapacity(state, index);
  const fed = Math.min(count, capacity);
  return {
    count,
    capacity,
    stranded: Math.max(0, count - fed),
    fraction: count === 0 ? 1 : fed / count,
  };
}

// ---------------------------------------------------------------------------
// Production
// ---------------------------------------------------------------------------

/**
 * Raw thoughts per second before any multiplier: the resting potential of the
 * brain itself, plus count x output x milestone bonus for every generator.
 */
export function baseTPS(state: GameState): Decimal {
  let total = new Decimal(BASE_OUTPUT);
  for (let i = 0; i < GENERATORS.length; i++) {
    const def = GENERATORS[i];
    if (def === undefined) continue;
    const count = state.generators[i] ?? 0;
    if (count === 0) continue;
    const units = effectiveUnits(count, supportCapacity(state, i));
    total = total.add(new Decimal(def.baseOutput).mul(units).mul(milestoneMult(count)));
  }
  return total;
}

/** Output contributed by one generator tier, for the per-row readout. */
export function tierTPS(state: GameState, index: number, mods: Modifiers): Decimal {
  const def = GENERATORS[index];
  if (def === undefined) return ZERO;
  const count = state.generators[index] ?? 0;
  if (count === 0) return ZERO;
  return new Decimal(def.baseOutput)
    .mul(effectiveUnits(count, supportCapacity(state, index)))
    .mul(milestoneMult(count))
    .mul(outputMultiplier(state, mods));
}

/** Output multiplier from clock speed. (clock / 100) ^ clockExp. */
export function clockMult(clock: number, exp: number = CLOCK_EXP): number {
  return Math.pow(clock / 100, exp);
}

/**
 * Output multiplier from heat. Deliberately superlinear: at heat 50 this is
 * only x1.15, at heat 99 it is x1.59. The reward for riding the redline
 * arrives almost entirely in the last few points of heat.
 */
export function thermalBonus(heat: number): number {
  const h = heat / 100;
  return 1 + h * h * THERMAL_MAX;
}

/**
 * Multiplier from equipped Components. Hook for the v1.2 loot expansion.
 * Constant 1 in v1 - keep it in the formula so adding loot is not a rewrite.
 */
export function componentMult(_state: GameState): Decimal {
  return ONE;
}

/** Everything that scales raw output, as one number. */
export function outputMultiplier(state: GameState, mods: Modifiers): Decimal {
  const idle = state.clock <= 100 ? mods.idleOutputMult : 1;
  return new Decimal(clockMult(state.clock, mods.clockExp))
    .mul(thermalBonus(state.heat))
    .mul(mods.tpsMult)
    .mul(idle)
    .mul(componentMult(state));
}

/** Thoughts produced per second, right now, with everything applied. */
export function tps(state: GameState, mods: Modifiers = getModifiers(state)): Decimal {
  return baseTPS(state).mul(outputMultiplier(state, mods));
}

/**
 * Production at a safe idle: clock 100, heat 0. Drives the offline payout,
 * which must never reward heat the player was not present to manage.
 */
export function idleTPS(state: GameState, mods: Modifiers): Decimal {
  const idle: GameState = { ...state, clock: 100, heat: 0 };
  return tps(idle, mods);
}

// ---------------------------------------------------------------------------
// Clock and heat
// ---------------------------------------------------------------------------

export function clockRange(mods: Modifiers): { min: number; max: number } {
  return { min: CLOCK_MIN, max: mods.clockCap };
}

/**
 * Heat per second from the cells themselves, weighted by where output comes
 * from. Leaning on the base tiers is expensive; the same sparks from higher up
 * cost roughly a quarter as much.
 */
export function cellHeat(state: GameState): number {
  let weighted = 0;
  let total = 0;
  for (let i = 0; i < GENERATORS.length; i++) {
    const def = GENERATORS[i];
    if (def === undefined) continue;
    const count = state.generators[i] ?? 0;
    if (count === 0) continue;
    // Raw share of output: the global multipliers cancel, so they are skipped.
    const share = new Decimal(def.baseOutput)
      .mul(effectiveUnits(count, supportCapacity(state, i)))
      .mul(milestoneMult(count))
      .toNumber();
    if (!Number.isFinite(share)) continue;
    total += share;
    weighted += share * def.heatFactor;
  }
  if (total <= 0) return 0;
  return CELL_HEAT * (weighted / total);
}

/** Heat units generated per second at the current clock. */
export function heatGen(clock: number, mods: Modifiers): number {
  return HEAT_BASE * Math.pow(clock / 100, mods.heatExp);
}

/**
 * Net heat change per second. Negative means the rig is cooling.
 *
 * HEAT_BASE and COOLING_BASE are equal by design, so clock 100 is exactly
 * break-even: 100% is a safe indefinite idle with no special-casing, and
 * every point above it is a countdown.
 */
export function netHeat(state: GameState, mods: Modifiers): number {
  return heatGen(state.clock, mods) + cellHeat(state) + voidHeat(state, mods) - mods.cooling;
}

/**
 * Seconds until meltdown at the current settings, or null if the rig is stable.
 *
 * Integrated rather than divided, because void pressure grows as the run goes
 * on: a flat projection would keep promising time the player does not have and
 * then melt early, which reads as the readout lying.
 */
export function secondsToMeltdown(state: GameState, mods: Modifiers): number | null {
  const STEP = 1;
  const LIMIT = 3600;

  let heat = state.heat;
  let elapsed = 0;
  let probe = state;

  while (elapsed < LIMIT) {
    const net =
      heatGen(probe.clock, mods) + cellHeat(probe) + voidHeat(probe, mods) - mods.cooling;
    // Stable only while the void is not yet adding anything; once it is, the
    // pressure keeps climbing and the run is on a clock whatever the dial says.
    if (net <= 0 && voidHeat(probe, mods) <= 0) return null;
    if (net > 0) {
      const remaining = HEAT_MAX - heat;
      if (remaining <= net * STEP) return elapsed + remaining / net;
      heat += net * STEP;
    }
    elapsed += STEP;
    probe = { ...probe, runTimeSec: probe.runTimeSec + STEP };
  }
  return null;
}

/** The lowest clock that would melt the rig from cold in under 30 seconds. */
export function redlineClock(mods: Modifiers): number {
  const needed = HEAT_MAX / 30 + mods.cooling;
  return 100 * Math.pow(needed / HEAT_BASE, 1 / mods.heatExp);
}

// ---------------------------------------------------------------------------
// The Void
// ---------------------------------------------------------------------------

/**
 * How much ground the intrusive thoughts have taken this run, 0 to just under 1.
 *
 * Silent for the first VOID_ONSET_SECONDS, then closes on even odds after
 * VOID_HOLD_SECONDS of contested time. Never reaches 1: the brain is always
 * still in there somewhere.
 */
export function voidShare(state: GameState, mods: Modifiers): number {
  if (state.stats.meltdowns < VOID_AFTER_MELTDOWNS) return 0;
  const contested = state.runTimeSec - VOID_ONSET_SECONDS;
  if (contested <= 0) return 0;
  const hold = VOID_HOLD_SECONDS * mods.voidResist;
  return contested / (contested + hold);
}

/** The brain's remaining share of itself, as a percentage for the readout. */
export function brainShare(state: GameState, mods: Modifiers): number {
  return 1 - voidShare(state, mods);
}

/**
 * Extra heat per second from holding the void off.
 *
 * Nothing until the void has the upper hand, then it climbs. This is what puts
 * a soft ceiling on run length: a run cannot simply be extended forever by
 * sitting at a safe clock.
 */
export function voidHeat(state: GameState, mods: Modifiers): number {
  const share = voidShare(state, mods);
  if (share <= VOID_PRESSURE_THRESHOLD) return 0;
  const over = (share - VOID_PRESSURE_THRESHOLD) / (1 - VOID_PRESSURE_THRESHOLD);
  return VOID_HEAT_MAX * over * over;
}

export type VoidState = 'QUIET' | 'STIRRING' | 'CROWDING' | 'OVERWHELMING';

export function voidState(share: number): VoidState {
  if (share <= 0.01) return 'QUIET';
  if (share < VOID_PRESSURE_THRESHOLD) return 'STIRRING';
  if (share < 0.75) return 'CROWDING';
  return 'OVERWHELMING';
}

export type HeatState = 'NOMINAL' | 'CAUTION' | 'CRITICAL';

export function heatState(heat: number): HeatState {
  if (heat >= 85) return 'CRITICAL';
  if (heat >= 60) return 'CAUTION';
  return 'NOMINAL';
}

// ---------------------------------------------------------------------------
// Meltdown
// ---------------------------------------------------------------------------

/**
 * Grey Matter a meltdown would yield right now.
 *   floor( GM_SCALE * (thoughtsThisRun / GM_DIV) ^ GM_EXP * gmMult )
 */
export function exactIdeaGain(state: GameState, mods: Modifiers): Decimal {
  if (state.thoughtsThisRun.lt(new Decimal(GM_MIN_THOUGHTS))) return ZERO;
  return state.thoughtsThisRun
    .div(new Decimal(GM_DIV))
    .pow(GM_EXP)
    .mul(GM_SCALE)
    .mul(mods.gmMult);
}

/**
 * Whole ideas a meltdown right now would pay, including progress carried over
 * from runs that ended short of a whole one.
 */
export function greyMatterGain(state: GameState, mods: Modifiers): Decimal {
  return exactIdeaGain(state, mods).add(state.ideaRemainder).floor();
}

/** Whether a meltdown right now would actually pay out. */
export function meltdownWorthwhile(state: GameState, mods: Modifiers): boolean {
  return greyMatterGain(state, mods).gt(ZERO);
}

/**
 * Thoughts a run must have produced to be worth `ideas` whole ideas.
 *
 * The inverse of greyMatterGain. Because the payout is floored, the run has to
 * clear a real threshold for each whole idea, and the player deserves to be
 * told what it is rather than watching a number sit at zero.
 */
export function thoughtsForIdeas(ideas: number, mods: Modifiers): Decimal {
  if (ideas <= 0) return ZERO;
  // gain = SCALE * (T / DIV)^EXP * mult, solved for T.
  const scaled = new Decimal(ideas).div(new Decimal(GM_SCALE).mul(mods.gmMult));
  // Nudged up by a hair. The inverse is exact in real arithmetic, but pow()
  // lands a fraction below the boundary, so the exact threshold floors back
  // down to the previous whole idea - a target the player could never hit.
  const needed = new Decimal(GM_DIV).mul(scaled.pow(1 / GM_EXP)).mul(1 + 1e-9);
  // The low-yield guard can bind before the curve does.
  const floor = new Decimal(GM_MIN_THOUGHTS);
  return needed.gt(floor) ? needed : floor;
}

export interface IdeaProgress {
  /** Whole ideas this run is worth right now. */
  current: Decimal;
  /** Thoughts still to earn before that number goes up by one. */
  remaining: Decimal;
  /** How far between the last threshold and the next, 0..1. */
  fraction: number;
}

/** Live progress toward the next whole idea. */
export function ideaProgress(state: GameState, mods: Modifiers): IdeaProgress {
  const current = greyMatterGain(state, mods);
  // Carried progress means this run needs that much less to cross the line.
  const carried = state.ideaRemainder.toNumber();
  const from = thoughtsForIdeas(current.toNumber() - carried, mods);
  const to = thoughtsForIdeas(current.toNumber() + 1 - carried, mods);

  const span = to.sub(from);
  const done = state.thoughtsThisRun.sub(from);
  const fraction = span.lte(0) ? 1 : clamp01(done.div(span).toNumber());
  const remaining = to.sub(state.thoughtsThisRun);

  return { current, remaining: remaining.gt(0) ? remaining : ZERO, fraction };
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Thoughts still needed before a meltdown yields anything at all.
 *
 * This is the threshold for the first whole idea, not the low-yield guard.
 * Counting down to the guard promised a payout that floor() then rounded away.
 */
export function thoughtsToFirstGM(state: GameState, mods: Modifiers): Decimal {
  const target = thoughtsForIdeas(1 - state.ideaRemainder.toNumber(), mods);
  const needed = target.sub(state.thoughtsThisRun);
  return needed.gt(0) ? needed : ZERO;
}
